import {
  Box,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useState } from "react"

import {
  INVENTORY_LOT_COLUMN_COUNT,
  INVENTORY_PALLET_COLUMN_COUNT,
  InventoryLotTableHeaderCells,
  InventoryLotTableRowCells,
  InventoryPalletTableHeaderCells,
  InventoryPalletTableRowCells,
} from "../../../components"
import { isLotLocked } from "../../../utils"

import type {
  InventoryPalletGroup,
  InventoryPalletGroupsSortField,
  InventoryPalletLot,
  SortingOrder,
} from "@/graphql/codegen/graphql"

const PALLET_SELECTION_COLUMN_COUNT = 1
const LOT_SELECTION_COLUMN_COUNT = 1

const SELECTION_MODES = {
  MULTI_LOT: "multiLot",
  SINGLE_PALLET: "singlePallet",
} as const

type SelectionMode = (typeof SELECTION_MODES)[keyof typeof SELECTION_MODES]

interface AdjustmentLotSelectionProps {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  selectedLotIds: Set<string>
  sortBy: InventoryPalletGroupsSortField
  sortDirection: SortingOrder
  uomName: string
  selectionMode?: SelectionMode
  onSelectionChange: (lotIds: Set<string>) => void
  onSortChange: (field: InventoryPalletGroupsSortField, direction: SortingOrder) => void
  onSearchChange: (search: string) => void
}

export const AdjustmentLotSelection: FC<AdjustmentLotSelectionProps> = ({
  palletGroups,
  isFetching,
  selectedLotIds,
  sortBy,
  sortDirection,
  uomName,
  selectionMode = SELECTION_MODES.MULTI_LOT,
  onSelectionChange,
  onSortChange,
  onSearchChange,
}) => {
  const [search, setSearch] = useState("")
  const isSinglePalletMode = selectionMode === SELECTION_MODES.SINGLE_PALLET
  const columnCount =
    PALLET_SELECTION_COLUMN_COUNT +
    INVENTORY_PALLET_COLUMN_COUNT +
    (isSinglePalletMode ? 0 : LOT_SELECTION_COLUMN_COUNT) +
    INVENTORY_LOT_COLUMN_COUNT

  const getPalletSelectionProps = useCallback(
    (pallet: InventoryPalletGroup) => {
      const selectableLots = pallet.lots.filter((lot) => !isLotLocked(lot))

      if (!selectableLots.length) {
        return { checked: false, indeterminate: false, disabled: true }
      }

      const selectedCount = selectableLots.filter((lot) => selectedLotIds.has(lot.rawInventoryId)).length

      return {
        checked: selectedCount === selectableLots.length,
        indeterminate: selectedCount > 0 && selectedCount < selectableLots.length,
        disabled: false,
      }
    },
    [selectedLotIds]
  )

  const handlePalletSelectionChange = useCallback(
    (pallet: InventoryPalletGroup) => {
      const selectableLots = pallet.lots.filter((lot) => !isLotLocked(lot))
      const isAllSelected =
        selectableLots.length > 0 && selectableLots.every((lot) => selectedLotIds.has(lot.rawInventoryId))

      if (isSinglePalletMode) {
        onSelectionChange(isAllSelected ? new Set() : new Set(selectableLots.map((lot) => lot.rawInventoryId)))
        return
      }

      const next = new Set(selectedLotIds)
      selectableLots.forEach((lot) => (isAllSelected ? next.delete(lot.rawInventoryId) : next.add(lot.rawInventoryId)))
      onSelectionChange(next)
    },
    [isSinglePalletMode, selectedLotIds, onSelectionChange]
  )

  const handleLotSelectionChange = useCallback(
    (lot: InventoryPalletLot) => {
      const next = new Set(selectedLotIds)

      if (next.has(lot.rawInventoryId)) {
        next.delete(lot.rawInventoryId)
      } else {
        next.add(lot.rawInventoryId)
      }

      onSelectionChange(next)
    },
    [selectedLotIds, onSelectionChange]
  )

  return (
    <Box className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400 p-4'>
      <Box className='mb-4 flex items-center justify-between'>
        <Typography variant='h6' className='font-bold leading-10'>
          {isSinglePalletMode ? "1. Select Pallet" : "1. Select Pallets and Lots"}
        </Typography>
        <TextField
          placeholder='Search by Reference #, Lot or Batch'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            onSearchChange(e.target.value)
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  <SearchOutlined fontSize='small' />
                </InputAdornment>
              ),
            },
          }}
          className='min-w-[360px]'
          data-testid='inventory.adjustment-lot-selection.search-field'
        />
      </Box>
      <Box className='relative flex min-h-0 flex-1'>
        {isFetching && (
          <Box className='absolute inset-0 top-[53px] z-10 flex items-center justify-center bg-white/60'>
            <CircularProgress size={48} />
          </Box>
        )}
        <TableContainer className='overflow-auto'>
          <Table stickyHeader>
            <TableHead className='bg-gray-100'>
              <TableRow>
                {/* Pallet checkbox column */}
                <TableCell className='w-12' />
                <InventoryPalletTableHeaderCells sort={{ sortBy, sortDirection, onSortChange }} />
                {/* Lot checkbox column */}
                {!isSinglePalletMode && <TableCell className='w-12' />}
                <InventoryLotTableHeaderCells sort={{ sortBy, sortDirection, onSortChange }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {!palletGroups.length && (
                <TableRow>
                  <TableCell colSpan={columnCount} align='center' className='py-8'>
                    No pallets found
                  </TableCell>
                </TableRow>
              )}

              {palletGroups.map((pallet) => {
                const palletSelectionProps = getPalletSelectionProps(pallet)
                const isPalletSelectionDisabled = palletSelectionProps.disabled

                return pallet.lots.map((lot, lotIndex) => {
                  const isFirstLot = lotIndex === 0
                  const isLotSelectionDisabled = isLotLocked(lot)

                  return (
                    <TableRow
                      key={lot.rawInventoryId}
                      className={isLotSelectionDisabled ? "bg-gray-100" : undefined}
                      data-testid={`inventory.adjustment-lot-selection.row-${lot.rawInventoryId}`}
                    >
                      {/* Pallet selection — only on first lot */}
                      {isFirstLot ? (
                        <Tooltip
                          title='All lots on this pallet are locked due to a pending adjustment, work order, lot selection, or open goods receipt.'
                          disableHoverListener={!isPalletSelectionDisabled}
                        >
                          <TableCell>
                            {isSinglePalletMode ? (
                              <Radio
                                size='small'
                                checked={palletSelectionProps.checked}
                                disabled={isPalletSelectionDisabled}
                                onClick={() => handlePalletSelectionChange(pallet)}
                                data-testid={`inventory.adjustment-lot-selection.pallet-radio-${pallet.referenceNbr}`}
                              />
                            ) : (
                              <Checkbox
                                size='small'
                                {...palletSelectionProps}
                                disabled={isPalletSelectionDisabled}
                                onChange={() => handlePalletSelectionChange(pallet)}
                                data-testid={`inventory.adjustment-lot-selection.pallet-checkbox-${pallet.referenceNbr}`}
                              />
                            )}
                          </TableCell>
                        </Tooltip>
                      ) : (
                        <TableCell className='h-[60px]' />
                      )}
                      <InventoryPalletTableRowCells pallet={pallet} isFirstLot={isFirstLot} uomName={uomName} />
                      {!isSinglePalletMode && (
                        <Tooltip
                          title='This lot is locked due to a pending adjustment, work order, lot selection, or open goods receipt.'
                          disableHoverListener={!isLotSelectionDisabled}
                        >
                          <TableCell>
                            <Checkbox
                              size='small'
                              checked={selectedLotIds.has(lot.rawInventoryId)}
                              disabled={isLotSelectionDisabled}
                              onChange={() => handleLotSelectionChange(lot)}
                              data-testid={`inventory.adjustment-lot-selection.lot-checkbox-${lot.rawInventoryId}`}
                            />
                          </TableCell>
                        </Tooltip>
                      )}
                      <InventoryLotTableRowCells lot={lot} uomName={uomName} />
                    </TableRow>
                  )
                })
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}
